import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
  ArrowLeft,
  Mail,
  Phone,
  Building,
  CreditCard,
  MessageSquare,
  Calendar,
  Activity,
  TrendingUp,
  RefreshCw,
  Ban,
  Settings,
  AlertTriangle,
  X,
  Users,
  Zap,
} from "lucide-react";
import { supabase } from "../integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { EditSubscriptionModal } from "../components/EditSubscriptionModal";
import { PaymentsTable } from "../components/PaymentsTable";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface ClientDetails {
  id: string;
  email: string;
  name: string;
  company?: string;
  phone?: string;
  plan: string;
  status: string;
  created_at: string;
  subscription_start?: string;
  subscription_end?: string;
}

export function ClientDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState<ClientDetails | null>(null);
  const [loading, setLoading] = useState(true);
 
 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [usageData, setUsageData] = useState<any[]>([]);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [subscription, setSubscription] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: "", message: "" });
  const [usageLoading, setUsageLoading] = useState(false);
  const statsLoading = false;
  const stats = { contacts: 0, conversations: 0 };
  const currentUsageLoading = false;
  const currentUsage = { messages_sent: 0, ai_responses_used: 0 };
  const limitsLoading = false;
  const limits = { messages_limit: 1000, ai_responses_limit: 500 };

  const fetchUsageData = async (userId: string) => {
    setUsageLoading(true);
    try {
      const { data, error } = await supabase
        .from("client_usage")
        .select("*")
        .eq("user_id", userId)
        .order("period_start", { ascending: false })
        .limit(6);
 
      if (error) throw error;
      if (data && data.length > 0) {
// eslint-disable-next-line @typescript-eslint/no-explicit-any
        const formatted = [...data].reverse().map((d: any) => {
          const date = d.period_start || d.created_at || d.month;
          return {
            month: date
              ? new Date(date).toLocaleDateString("pt-BR", { month: "short" })
              : "N/A",
            messages: d.messages_used ?? d.messages_sent ?? 0,
            ai: d.ai_responses_used ?? d.ai_responses_generated ?? 0,
          };
        });
        setUsageData(formatted);
      } else {
        setUsageData([]);
      }
    } catch (err) {
      console.error("Erro ao buscar uso:", err);
      setUsageData([]);
    } finally {
      setUsageLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
 
      fetchClientDetails(id);
      fetchUsageData(id);
    }
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchClientDetails = async (userId: string) => {
    setLoading(true);
    try {
      // Fetch subscription
      const { data: subscriptionData } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      setSubscription(subscriptionData);

      // Fetch settings
      const { data: settings } = await supabase
        .from("user_settings")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      setClient({
        id: userId,
        email: (settings as { email?: string })?.email ?? "",
        name: settings?.display_name || "Cliente",
        company: settings?.company,
        phone: settings?.phone,
        plan: subscription?.plan || "starter",
        status: subscription?.status || "active",
        created_at: subscription?.created_at || new Date().toISOString(),
        subscription_start: subscription?.current_period_start,
        subscription_end: subscription?.current_period_end,
      });
    } catch (error) {
      console.error("Error fetching client details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!id || !client) return;
    if (!confirm(`Suspender cliente ${client.name}?`)) return;
    try {
      const { error } = await supabase
        .from("subscriptions")
        .update({ status: "suspended" })
        .eq("user_id", id);
      if (error) throw error;
      toast.success("Cliente suspenso");
      fetchClientDetails(id);
    } catch {
      toast.error("Erro ao suspender");
    }
  };

  const handleAdjustLimits = () => {
    navigate("/limits");
    toast.info("Funcionalidade em desenvolvimento");
  };

  const handleSendEmail = () => {
    const email = client?.email || "";
    const subject = encodeURIComponent(emailForm.subject);
    const body = encodeURIComponent(emailForm.message);
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, "_blank");
    toast.success("Cliente de email aberto");
    setShowEmailModal(false);
    setEmailForm({ subject: "", message: "" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-slate-400">
        <AlertTriangle className="w-12 h-12 mb-4" />
        <p>Cliente não encontrado</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/clients")}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-400" />
        </button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-white mb-1">{client.name}</h1>
          <p className="text-slate-400">{client.email}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowEditModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-slate-300 hover:bg-white/10 transition-all"
          >
            <Settings className="w-4 h-4" />
            Gerenciar Subscrição
          </button>
          <button
            type="button"
            onClick={handleSuspend}
            className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 hover:bg-red-500/20 transition-all"
          >
            <Ban className="w-4 h-4" />
            Suspender
          </button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <CreditCard className="w-5 h-5 text-blue-400" />
            </div>
            <span className="text-sm text-slate-400">Plano</span>
          </div>
          <p className="text-2xl font-bold text-white capitalize">
            {client.plan}
          </p>
          <p className="text-sm text-slate-500 mt-1">
            Status: <span className="text-emerald-400">{client.status}</span>
          </p>
        </div>

        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <MessageSquare className="w-5 h-5 text-emerald-400" />
            </div>
            <span className="text-sm text-slate-400">Mensagens</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {currentUsageLoading
              ? "..."
              : currentUsage?.messages_sent?.toLocaleString() || "0"}
          </p>
          <p className="text-sm text-slate-500 mt-1">
            de{" "}
            {limitsLoading
              ? "..."
              : limits?.messages_limit?.toLocaleString() || "1000"}{" "}
            no plano
          </p>
          <div className="mt-2 h-2 bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{
                width: `${Math.min(100, ((currentUsage?.messages_sent || 0) / (limits?.messages_limit || 1000)) * 100)}%`,
              }}
            />
          </div>
        </div>

        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <Activity className="w-5 h-5 text-purple-400" />
            </div>
            <span className="text-sm text-slate-400">Respostas IA</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {currentUsageLoading
              ? "..."
              : currentUsage?.ai_responses_used?.toLocaleString() || "0"}
          </p>
          <p className="text-sm text-slate-500 mt-1">
            de{" "}
            {limitsLoading
              ? "..."
              : limits?.ai_responses_limit?.toLocaleString() || "500"}{" "}
            no plano
          </p>
          <div className="mt-2 h-2 bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-purple-500 transition-all"
              style={{
                width: `${Math.min(100, ((currentUsage?.ai_responses_used || 0) / (limits?.ai_responses_limit || 500)) * 100)}%`,
              }}
            />
          </div>
        </div>

        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <span className="text-sm text-slate-400">Contatos</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {statsLoading ? "..." : stats?.contacts?.toLocaleString() || "0"}
          </p>
          <p className="text-sm text-slate-500 mt-1">Total cadastrados</p>
        </div>

        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-cyan-500/20">
              <Zap className="w-5 h-5 text-cyan-400" />
            </div>
            <span className="text-sm text-slate-400">Conversas</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {statsLoading
              ? "..."
              : stats?.conversations?.toLocaleString() || "0"}
          </p>
          <p className="text-sm text-slate-500 mt-1">Threads ativos</p>
        </div>

        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Calendar className="w-5 h-5 text-amber-400" />
            </div>
            <span className="text-sm text-slate-400">Cliente desde</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {format(new Date(client.created_at), "dd/MM/yy")}
          </p>
          <p className="text-sm text-slate-500 mt-1">
            {format(new Date(client.created_at), "MMMM yyyy", { locale: ptBR })}
          </p>
        </div>
      </div>

      {/* Charts and Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Usage Chart */}
        <div className="lg:col-span-2 bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-6">Uso Mensal</h3>
          {usageLoading ? (
            <div className="h-64 flex items-center justify-center">
              <span className="text-slate-400">Carregando...</span>
            </div>
          ) : usageData.length === 0 ? (
            <div className="h-64 flex items-center justify-center">
              <span className="text-slate-400">
                Sem dados de uso disponíveis
              </span>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={usageData}>
                <defs>
                  <linearGradient
                    id="colorMessages"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorAI" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" />
                <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#0a0a0a",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "8px",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="messages"
                  stroke="#ef4444"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorMessages)"
                  name="Mensagens"
                />
                <Area
                  type="monotone"
                  dataKey="ai"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorAI)"
                  name="Respostas IA"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Client Info */}
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-6">Informações</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
              <Mail className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Email</p>
                <p className="text-sm text-white">
                  {client.email || "Email não disponível"}
                </p>
              </div>
            </div>

            {client.phone && (
              <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                <Phone className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Telefone</p>
                  <p className="text-sm text-white">{client.phone}</p>
                </div>
              </div>
            )}

            {client.company && (
              <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                <Building className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Empresa</p>
                  <p className="text-sm text-white">{client.company}</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
              <Calendar className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Próxima Renovação</p>
                <p className="text-sm text-white">
                  {client.subscription_end
                    ? format(new Date(client.subscription_end), "dd/MM/yyyy")
                    : "N/A"}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-white/10">
            <h4 className="text-sm font-medium text-slate-400 mb-3">
              Ações Rápidas
            </h4>
            <div className="space-y-2">
              <button
                onClick={() => setShowEditModal(true)}
                className="w-full flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg text-slate-300 hover:bg-white/10 transition-all text-sm"
              >
                <TrendingUp className="w-4 h-4" />
                Gerenciar Subscrição
              </button>
              <button
                type="button"
                onClick={handleAdjustLimits}
                className="w-full flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg text-slate-300 hover:bg-white/10 transition-all text-sm"
              >
                <Settings className="w-4 h-4" />
                Ajustar Limites
              </button>
              <button
                type="button"
                onClick={() => setShowEmailModal(true)}
                className="w-full flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg text-slate-300 hover:bg-white/10 transition-all text-sm"
              >
                <Mail className="w-4 h-4" />
                Enviar Email
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Histórico de Pagamentos */}
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white mb-6">
          Histórico de Pagamentos
        </h3>
        <PaymentsTable userId={id || ""} />
      </div>

      {showEmailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowEmailModal(false)}
          />
          <div className="relative bg-[#0a0a0a] border border-white/10 rounded-xl p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Enviar Email</h2>
              <button
                type="button"
                onClick={() => setShowEmailModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Para</label>
                <input
                  type="email"
                  readOnly
                  value={client?.email || "Email não disponível"}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  Assunto
                </label>
                <input
                  type="text"
                  value={emailForm.subject}
                  onChange={(e) =>
                    setEmailForm((prev) => ({
                      ...prev,
                      subject: e.target.value,
                    }))
                  }
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
                  placeholder="Assunto do email"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  Mensagem
                </label>
                <textarea
                  rows={5}
                  value={emailForm.message}
                  onChange={(e) =>
                    setEmailForm((prev) => ({
                      ...prev,
                      message: e.target.value,
                    }))
                  }
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white resize-none"
                  placeholder="Digite sua mensagem..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEmailModal(false)}
                  className="flex-1 px-4 py-2 border border-white/10 rounded-lg text-white hover:bg-white/5"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSendEmail}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white"
                >
                  Enviar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <EditSubscriptionModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        userId={client.id}
        subscription={subscription}
        onSuccess={() => id && fetchClientDetails(id)}
      />
    </div>
  );
}
